import { Component, ViewEncapsulation } from '@angular/core';
import { IFloatingFilterAngularComp } from 'ag-grid-angular';
import { IFloatingFilterParams } from 'ag-grid-community';

@Component({
  selector: 'controlled-text-floating-filter',
  host: {
    class: 'ag-floating-filter-full-body',
    role: 'presentation',
  },
  template: `
    <div class="ag-floating-filter-input" role="presentation">
      <div class="ag-text-field ag-input-field" role="presentation">
        <div class="ag-input-field-label"></div>
        <div class="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper" role="presentation">
          <input
            class="ag-input-field-input ag-text-field-input"
            type="text"
            [value]="currentValue"
            [attr.aria-label]="ariaLabel"
            autocomplete="off"
            (input)="onInput($event)"
            (keydown)="onKeyDown($event)"
          />
        </div>
      </div>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class ControlledTextFloatingFilterComponent implements IFloatingFilterAngularComp {
  params: IFloatingFilterParams<any>;
  currentValue = '';
  ariaLabel = 'Filter Input';

  agInit(params: IFloatingFilterParams<any>): void {
    this.params = params;
    this.updateAriaLabel();
    this.syncFromParentModel(params.currentParentModel?.());
  }

  onParentModelChanged(parentModel: any): void {
    this.syncFromParentModel(parentModel);
  }

  refresh(params: IFloatingFilterParams<any>): void {
    this.params = params;
    this.updateAriaLabel();
    this.syncFromParentModel(params.currentParentModel?.());
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.currentValue = target?.value ?? '';
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== 'NumpadEnter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    this.applyCurrentValue();
  }

  private applyCurrentValue(): void {
    const value = this.normalizeValue(this.currentValue);
    const filterType = this.resolveFilterType();
    this.currentValue = value;

    this.params.parentFilterInstance((filterInstance: any) => {
      if (!filterInstance?.onFloatingFilterChanged) {
        return;
      }

      filterInstance.onFloatingFilterChanged(filterType, value || null);
    });
  }

  private syncFromParentModel(parentModel: any): void {
    this.currentValue = parentModel?.filter?.toString?.() || '';
  }

  private normalizeValue(value: string): string {
    const rawValue = value ?? '';
    const filterParams = this.getTextFilterParams();
    if (filterParams?.trimInput) {
      return rawValue.trim();
    }

    return rawValue;
  }

  private resolveFilterType(): string {
    const filterOptions = this.getTextFilterParams()?.filterOptions;
    if (Array.isArray(filterOptions) && filterOptions.length > 0) {
      return filterOptions[0];
    }

    return 'contains';
  }

  private getTextFilterParams(): { trimInput?: boolean; filterOptions?: string[] } | null {
    return (this.params?.filterParams as { trimInput?: boolean; filterOptions?: string[] } | undefined) || null;
  }

  private updateAriaLabel(): void {
    const headerName = this.params?.column?.getColDef?.()?.headerName || 'Filter';
    this.ariaLabel = `${headerName} Filter Input`;
  }
}